/* eslint-disable react/jsx-props-no-spreading */
import {useEffect} from 'react';

import {TabContext} from '@mui/lab';
import Box from '@mui/material/Box';
import muiStyled from '@mui/material/styles/styled';
import {useLocation} from 'react-router-dom';

import CreateWorkspaceButton from './CreateWorkspaceButton';
import {DEFAULT_WORKSPACE} from '../../const';
import usePayment from '../../hooks/providers/usePayment';
import useProfile from '../../hooks/providers/useProfile';
import useProjects from '../../hooks/providers/useProjects';
import useWorkspaces from '../../hooks/providers/useWorkspaces';
import CreateOrUpdateWorkspaceModal from '../workspaces/CreateOrUpdateWorkspaceModal';
import DeleteWorkspaceModal from '../workspaces/DeleteWorkspaceOrReportModal';
import WorkspacesTabsMenu from '../workspaces/WorkspacesTabsMenu';
import OverflowedTabs from './OverflowedTabs/OverflowedTabs';
import OverflowedTabsMenu from './OverflowedTabsMenu';
import StyledTab from './StyledTab';
import StyledTabs from './StyledTabs';
import TabLabel from './TabLabel';

const TabsContainer = muiStyled(Box)(({theme}) => ({
  borderBottom: '1px solid #E2E7EF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  padding: 0,
  [theme.breakpoints.down('md')]: {
    padding: 0
  }
}));

const WorkspacesTabs = () => {
  const {projects} = useProjects();
  const {isGuest, isAdmin} = usePayment();
  const {
    hiddenWorkspacesTabs,
    workspacesTabsToDisplay,
    setWorkspacesTabsToDisplay,
    isWorkspaceCurrentlyDeleting,
    workspaces,
    selectedWorkspaceId,
    setSelectedWorkspace,
    openWorkspaceTabsMenu,
    rightClickedWorkspace,
    setRightClickedWorkspaceId
  } = useWorkspaces();
  const {profile} = useProfile();
  const location = useLocation();

  const handleTabRightClick = (e, workspace) => {
    e.preventDefault();
    setRightClickedWorkspaceId(workspace.workspace_id);
    openWorkspaceTabsMenu(e);
  };

  const handleChange = (event, newSelectedWorkspaceId) => {
    setSelectedWorkspace(newSelectedWorkspaceId);
  };

  const shouldDisplayDefaultWorkspace = !isGuest || (isGuest && projects.length > 0);
  const workspacesWithoutDefaultWorkspace = workspaces.filter(w => w.workspace_id !== DEFAULT_WORKSPACE.workspace_id);
  const accessibleWorkspaces = shouldDisplayDefaultWorkspace ? workspaces : workspacesWithoutDefaultWorkspace;

  useEffect(() => {
    if (location.state?.workspaceId) {
      setSelectedWorkspace(location.state.workspaceId);
    }
  }, [location.state]);

  useEffect(() => {
    if (!shouldDisplayDefaultWorkspace && accessibleWorkspaces.length > 0) {
      const firstWorkspace = accessibleWorkspaces[0];
      if (firstWorkspace.workspace_id !== selectedWorkspaceId) {
        setSelectedWorkspace(firstWorkspace.workspace_id);
      }
    }
  }, [accessibleWorkspaces.length, shouldDisplayDefaultWorkspace]);

  useEffect(() => {
    if (!shouldDisplayDefaultWorkspace) {
      setWorkspacesTabsToDisplay(workspaces.filter(w => w.workspace_id !== DEFAULT_WORKSPACE.workspace_id));
    } else {
      setWorkspacesTabsToDisplay(workspaces.slice(0, 2));
    }
  }, [workspaces.length, shouldDisplayDefaultWorkspace]);

  useEffect(() => {
    if (workspaces.length === 0) return;

    if (shouldDisplayDefaultWorkspace) {
      setSelectedWorkspace(DEFAULT_WORKSPACE.workspace_id);
    }
  }, [workspaces.length, location.pathname]);

  if (!selectedWorkspaceId || accessibleWorkspaces.length === 0) return null;

  const workspacesStyledTabs = workspacesTabsToDisplay.map(workspace => {
    const {workspace_name: workspaceName, workspace_id: workspaceId, workspace_users: users} = workspace;
    const currentUser = users?.find(u => u.user_id.toLowerCase() === profile?.id.toLowerCase());
    const isCurrentUserWorkspaceOwner = currentUser?.is_owner;

    const sanitizedWorkspaceName = workspaceName.replace(/_/g, ' ');
    const maximumNumberOfChars = 25;
    const ellipsedWorkspaceName = sanitizedWorkspaceName.length > maximumNumberOfChars ? `${sanitizedWorkspaceName.slice(0, maximumNumberOfChars)}...` : sanitizedWorkspaceName;

    const isDefaultWs = workspaceId === DEFAULT_WORKSPACE.workspace_id;
    const isTabActive = selectedWorkspaceId === workspaceId;
    const canOpenMenu = isAdmin || (isCurrentUserWorkspaceOwner && !isDefaultWs);
    const onIconClick = canOpenMenu ? handleTabRightClick : null;
    const isLabelWithoutMenuFeature = !isAdmin && (isDefaultWs || !isCurrentUserWorkspaceOwner);
    const isDisabled = isWorkspaceCurrentlyDeleting(workspaceId);

    const label = isLabelWithoutMenuFeature ? (
      ellipsedWorkspaceName
    ) : (
      <TabLabel disabled={isDisabled} onIconClick={e => onIconClick(e, workspace)} label={ellipsedWorkspaceName} isActive={isTabActive} />
    );

    return <StyledTab wrapped key={workspaceId} isReportTab={false} disabled={isDisabled} label={label} value={workspaceId} onContextMenu={e => canOpenMenu && handleTabRightClick(e, workspace)} />;
  });

  return (
    <Box
      sx={{
        typography: 'body1',
        width: '100%',
        position: 'sticky',
        top: 64,
        zIndex: 99,
        background: 'white'
      }}
    >
      <TabContext value={selectedWorkspaceId}>
        <TabsContainer>
          <OverflowedTabs
            visibleItems={workspacesTabsToDisplay}
            overflowedItems={shouldDisplayDefaultWorkspace ? hiddenWorkspacesTabs : hiddenWorkspacesTabs.filter(w => w.workspace_id !== DEFAULT_WORKSPACE.workspace_id)}
            rightToolbar={<CreateWorkspaceButton />}
            renderMenu={({anchorEl, isOpen, onClose, overflowedItems}) => (
              <OverflowedTabsMenu
                tabs={overflowedItems
                  .sort((a, b) => a.workspace_name.localeCompare(b.workspace_name))
                  .map(w => ({
                    value: w.workspace_id,
                    label: w.workspace_name.replace(/_/g, ' ')
                  }))}
                entityName="espace de travail"
                onItemSelect={wsId => {
                  const hasDefaultWorkspace = workspaces.find(w => w.workspace_id === DEFAULT_WORKSPACE.workspace_id) !== undefined && shouldDisplayDefaultWorkspace;
                  const selectedWorkspace = workspacesWithoutDefaultWorkspace.find(w => w.workspace_id === wsId);
                  selectedWorkspace.isSelected = true;

                  if (hasDefaultWorkspace) {
                    setWorkspacesTabsToDisplay([DEFAULT_WORKSPACE, selectedWorkspace]);
                  } else {
                    const secondWorkspace = workspacesWithoutDefaultWorkspace.filter(w => w.workspace_id !== selectedWorkspace.workspace_id)[0];
                    setWorkspacesTabsToDisplay([selectedWorkspace, secondWorkspace]);
                  }
                  setSelectedWorkspace(wsId);
                }}
                anchorEl={anchorEl}
                isOpen={isOpen}
                onClose={onClose}
              />
            )}
          >
            <StyledTabs variant="scrollable" scrollButtons={false} onChange={handleChange} aria-label="Sélecteur d'espace de travail">
              {workspacesStyledTabs}
            </StyledTabs>
            {rightClickedWorkspace ? <WorkspacesTabsMenu workspace={rightClickedWorkspace} /> : null}
          </OverflowedTabs>
        </TabsContainer>
      </TabContext>

      <DeleteWorkspaceModal />

      <CreateOrUpdateWorkspaceModal />
    </Box>
  );
};

export default WorkspacesTabs;
